<template>
  <div>
    <h3>Graduate: {{ graduate.name }}</h3>

    <div class="card">
      <div class="card-header">
        <a class data-toggle="collapse" href="#collapseDirections" role="button" aria-expanded="false" aria-controls="collapseDirections">
          <div class="row">
            <div class="col">
              <h3 class="card-header-title">Directions</h3>
            </div>
            <div class="col-auto">
              <span class="collapse-marker">
                Hide / Show
                <font-awesome :icon="['fas', 'chevron-down']"/>
              </span>
            </div>
          </div><!-- /.row -->
        </a>
      </div>
      <div class="collapse show" id="collapseDirections">
        <div class="card-body card-body-info">
          <p>Please enter your assessment of the graduate’s capabilities at the end of the placement.</p>

          <p>
            <ul>
              <li>
                <b>{{ strengthText }}:</b> All behavioural indicators were demonstrated, clearly demonstrating capability at this level.
              </li>
              <li>
                <b>Meets requirements:</b> A majority of behavioural indicators were demonstrated, meeting the pre-established standards for this capability.
              </li>
              <li>
                <b>Development required:</b> Few behavioural indicators were demonstrated, indicating development is required on this capability.
              </li>
              <li>
                <b>Not observed:</b> The capability was not observed.
              </li>
            </ul>
          </p>

          <p>
            The graduate’s development objectives are provided below for your information to assist with your review. Select to Hide/Show this section on the right hand side.
          </p>
        </div>
      </div><!-- /.collapse -->
    </div><!-- /.card -->

    <div class="card">
      <div class="card-header">
        <a class="" data-toggle="collapse" href="#collapseDevelopmentObjectives" role="button" aria-expanded="false" aria-controls="collapseDevelopmentObjectives">
          <div class="row">
            <div class="col">
              <h3 class="card-header-title">
                Graduate Development Objectives
              </h3>
            </div>
            <div class="col-auto">
              <span class="collapse-marker">Hide / Show <font-awesome :icon="['fas', 'chevron-down']"></font-awesome></span>
            </div>
          </div><!-- /.row -->
        </a>
      </div>
      <div class="collapse show" id="collapseDevelopmentObjectives">
        <placements-development-item-read-only v-for="objective in objectives" :objective="objective" :key="objective.id"/>
      </div><!-- /.collapse -->
    </div><!-- /.card -->

    <div class="card">
      <div class="card-header">
        <h3 class="card-header-title">Capabilities Assessment</h3>
      </div>
    </div>

    <loading-spinner v-if="fetching"/>

    <capability-group v-else v-for="group in groupedCapabilities" :group="group" :key="group.id"/>

    <div class="alert alert-danger" v-if="errors && errors.items.length > 0">
      <p>All capability results must be selected before proceeding to the next step.</p>
    </div>

    <div class="save-actions">
      <div class="text-right">
        <ladda-button :click="discardAndExit" class="btn btn-link btn-discard mr-2">Discard &amp; Exit</ladda-button>
        <ladda-button :click="handleSaveAndExit" :validate="false" class="btn btn-outline-dark mr-2">Save &amp; Exit</ladda-button>
        <ladda-button :click="handleNext" :validate="true" class="btn btn-success">Next</ladda-button>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<script>
import { placementComputed, placementMethods, graduateComputed, placementCapabilitiesComputed, placementCapabilitiesMethods, placementDevelopmentObjectiveComputed, placementDevelopmentObjectiveMethods } from 'vx/helpers'
import { handleServerErrors, alert } from 'utils'
import CapabilityGroup from './PlacementsEndComponentCapabilitiesGroup'
import PlacementsDevelopmentItemReadOnly from './PlacementsDevelopmentItemReadOnly'

export default {
  components: {
    CapabilityGroup,
    PlacementsDevelopmentItemReadOnly
  },

  data () {
    return {
      fetching: false
    }
  },

  computed: {
    ...placementComputed,
    ...graduateComputed,
    ...placementCapabilitiesComputed,
    ...placementDevelopmentObjectiveComputed
  },

  async created () {
    this.fetching = true

    await this.getDevelopmentObjectives()
    await this.getCapabilities({
      graduate: this.$route.params.graduateId,
      placement: this.$route.params.placementId
    })

    alert.info({
      title: '',
      button: 'Proceed',
      text: 'Please note the descriptions in the Directions box to ensure an accurate rating of your graduate’s capabilities at the end of this placement.\n \n Strength is the highest rating, and Development Required is the lowest. Not observed can be used if the graduate was not provided the opportunity to demonstrate the capability, or if it was not required in their role.'
    })

    this.fetching = false
  },

  methods: {
    ...placementCapabilitiesMethods,
    ...placementDevelopmentObjectiveMethods,
    ...placementMethods,

    async discardAndExit () {
      const confirm = await alert.confirm({
        text: 'Are you sure you want to leave? Any unsaved changes will be lost.'
      })

      if (confirm) {
        this.$router.push({ name: 'graduates.placements.single', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
      }
    },

    async handleNext () {
      let payload = {
        capabilities: this.capabilities,
        draft: false
      }

      try {
        await this.storeCapabilities({ payload })

        this.$router.push({ name: 'placements.single.end.manager.overall', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
        this.setPlacementProperty({ property: 'end_component.capability_assessment_completed', value: true })
        this.$noty.success('Capabilities Saved')
      } catch (error) {
        handleServerErrors(error, this.$validator)
      }
    },

    async handleSaveAndExit () {
      let payload = {
        capabilities: this.capabilities,
        draft: true
      }

      await this.storeCapabilities({ payload })
      this.$validator.reset()
      this.$noty.success('Capabilities Saved')
      this.$router.push({ name: 'graduates.placements.single', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
    }
  }
}
</script>
